/**
 * summarize.ts —— 「整合文件」的第三段：AI 摘要
 *
 * 设计文档 5.1 的三段式里，第 ③ 段（AI 生成摘要）单独放这里，而不是写进 file.ts。
 * 原因：file.ts 是纯数据层能力，不该依赖 AI 模块。这样拆分后：
 *   · AI 没配 / 挂了 → file.summarize() 照样落库（前两段完成），只是 summary 空着
 *   · 用户之后随时可以说「把那份摘要补上」，再走一次这里
 *
 * 这一段是**唯一一处允许 AI 输出长文本**的地方 —— 因为它的产物是
 * 「用户自己要看的内容」，不是「对执行的断言」。所以不违反信任原则：
 * 它描述的是**刚读到的真实文件清单**（模型确实拿到了这些数据），
 * 而不是"我刚才执行成功了没有"。
 */

import { chat, AIError } from './client';
import { readDigest, writeDigestSummary, renderDigestForAI } from '@/il/api/file';

const SYSTEM = `你是一个帮用户整理聊天文件的助手。用户会给你一份文件的清单，请你用中文写一段简短的摘要。

要求：
· 直接写摘要正文，不要任何前缀（不要写「好的」「以下是摘要」）
· 2~4 句话，说清楚：一共多少个文件、都是些什么类型、有没有值得注意的（比如文件很多、体积很大、集中在某段时间）
· 不要逐条罗列文件名（清单用户自己能看）
· 如果文件很少（比如只有一两个），就一两句话带过，不要硬凑
· 语气平实，不要用「您」也不要过分热情`;

/**
 * 为一条摘要记录生成 AI 摘要并回填。
 *
 * 返回值用于给用户的提示：
 *   ok: true  → 摘要已生成并写入
 *   ok: false → 生成失败，但**记录本身仍在**（清单已落库），给出原因
 */
export async function summarizeDigest(
  digestId: string,
): Promise<{ ok: boolean; summary: string; reason?: string }> {
  const rendered = await renderDigestForAI(digestId);
  if (rendered === undefined) {
    return { ok: false, summary: '', reason: '找不到这条整理记录' };
  }

  try {
    const summary = await chat(SYSTEM, rendered);
    const text = summary.trim();
    if (!text) return { ok: false, summary: '', reason: 'AI 返回了空摘要' };
    await writeDigestSummary(digestId, text);
    return { ok: true, summary: text };
  } catch (err) {
    const reason = err instanceof AIError ? err.reason : String(err);
    return { ok: false, summary: '', reason };
  }
}

/** 取一条摘要记录的当前内容（可能还没生成） */
export async function readSummary(digestId: string): Promise<string> {
  const d = await readDigest(digestId);
  return d?.summary ?? '';
}
